import Image from "next/image";
import Link from "next/link";

import { NEXT_URL } from "@/utils/const";

import { REFERRAL_NOTICE_TONE } from "./referralNotice";
import { getReferralBonusProgressMessage } from "./referralHelpers";
import type { ReferralNoticeTone } from "./types";
import { useReferralOverview } from "./useReferralOverview";

const HeartProgress = ({
  current,
  target,
  tone,
}: {
  current: number;
  target: number;
  tone: ReferralNoticeTone;
}) => {
  if (tone === REFERRAL_NOTICE_TONE.GOLD && current >= target) {
    return (
      <div className="flex shrink-0 items-center">
        <Image
          src="/user/img/referral/referral-trophy.svg"
          alt=""
          aria-hidden="true"
          width={32}
          height={32}
          className="h-8 w-8 shrink-0"
        />
        <span className="bg-gradient-to-b from-[#FBE17B] to-[#D49728] bg-clip-text font-['Inter'] text-lg font-bold leading-none text-transparent">
          達成
        </span>
      </div>
    );
  }

  return (
    <div
      className={`flex shrink-0 items-center ${
        tone === REFERRAL_NOTICE_TONE.TEAL ? "gap-0" : "gap-1"
      }`}
    >
      {Array.from({ length: target }, (_, index) => {
        const isFilled = index < current;

        if (tone === REFERRAL_NOTICE_TONE.TEAL) {
          return isFilled ? (
            <Image
              key={index}
              src="/user/img/referral/referral-heart-filled.png"
              alt=""
              aria-hidden="true"
              width={43}
              height={32}
              className="h-8 w-[43px] shrink-0"
            />
          ) : (
            <Image
              key={index}
              src="/user/img/referral/referral-heart.png"
              alt=""
              aria-hidden="true"
              width={43}
              height={32}
              className="h-8 w-[43px] shrink-0"
            />
          );
        }

        return (
          <Image
            key={index}
            src={
              index === target - 1
                ? "/user/img/referral/referral-crown-achieved.png"
                : "/user/img/referral/referral-crown.png"
            }
            alt=""
            aria-hidden="true"
            width={index === target - 1 ? 77 : 32}
            height={32}
            className={`h-8 shrink-0 ${
              index === target - 1 ? "w-[77px]" : "w-8"
            } ${index === target - 1 || isFilled ? "" : "opacity-30"}`}
          />
        );
      })}
    </div>
  );
};

const ProgressContent = ({
  value,
  current,
  target,
  tone,
  message,
  grow = false,
}: {
  value: number;
  current: number;
  target: number;
  tone: ReferralNoticeTone;
  message: string;
  grow?: boolean;
}) => (
  <div
    className={`flex flex-col justify-center ${
      grow ? "min-w-0 flex-1" : "shrink-0"
    }`}
  >
    <div className="flex items-center gap-3">
      <div
        className={`relative h-14 shrink-0 ${
          tone === REFERRAL_NOTICE_TONE.TEAL
            ? value >= 10
              ? "w-[76px]"
              : value === 3
              ? "w-[49px]"
              : "w-[46px]"
            : "w-[46px]"
        }`}
      >
        <span
          className={`absolute left-0 top-0 text-[48px] font-bold leading-none ${
            tone === REFERRAL_NOTICE_TONE.TEAL
              ? "text-[#45BEAB]"
              : "bg-gradient-to-b from-[#FBE17B] to-[#D49728] bg-clip-text text-transparent"
          } font-['Roboto'] leading-[normal]`}
        >
          {value}
        </span>
        <span
          className={`absolute top-[26px] font-['Inter'] text-base font-bold leading-[normal] ${
            tone === REFERRAL_NOTICE_TONE.TEAL
              ? value >= 10
                ? "left-[59px]"
                : value === 3
                ? "left-8"
                : "left-[29px]"
              : "left-[29px]"
          } ${
            tone === REFERRAL_NOTICE_TONE.TEAL
              ? "text-[#45BEAB]"
              : "bg-gradient-to-b from-[#FBE17B] to-[#D49728] bg-clip-text text-transparent"
          }`}
        >
          人
        </span>
      </div>
      <HeartProgress current={current} target={target} tone={tone} />
    </div>
    <p className="-mt-1 font-['Inter'] text-xs font-medium leading-[18px] text-[#757575]">
      {message}
    </p>
  </div>
);

export const ReferralSummary = () => {
  const { data, error } = useReferralOverview();

  if (error || !data?.canRefer) return null;

  const paidRemaining = data.stats.paidMilestoneRemaining;

  return (
    <section className="rounded-[10px] bg-[#ECFAF7] md:w-[1200px] md:max-w-none">
      <div className="grid divide-y divide-white md:h-[100px] md:grid-cols-[350px_350px_minmax(0,1fr)] md:divide-y-0">
        <Link
          href={NEXT_URL.ACCOUNTS_REFERRAL}
          className="flex min-h-[144px] items-center gap-3 px-5 py-5 hover:bg-[#E1F7F4] md:min-h-0 md:gap-4 md:px-6 md:py-0"
        >
          <Image
            src="/user/img/referral/referral-illustration.svg"
            alt=""
            width={60}
            height={40}
            className="h-[40px] w-[60px] shrink-0 object-contain md:h-10 md:w-[60px]"
          />
          <div className="flex w-[208px] min-w-0 flex-col gap-1">
            <div className="flex items-center gap-2">
              <p className="w-[176px] whitespace-nowrap font-['Inter'] text-base font-bold leading-[normal] tracking-[-0.6px] text-[#000000]">
                ヨヤクー紹介プログラム
              </p>
              <span aria-hidden="true" className="relative h-6 w-6 shrink-0">
                <svg
                  viewBox="0 0 296 480"
                  className="absolute left-2 top-1.5 h-3 w-[7.4px]"
                  fill="none"
                >
                  <path
                    d="M184 240 0 56l56-56 240 240L56 480 0 424l184-184Z"
                    fill="#41C9B5"
                  />
                </svg>
              </span>
            </div>
            <p className="font-['Inter'] text-xs font-medium leading-[18px] text-[#757575]">
              紹介するほどお得に
            </p>
          </div>
        </Link>

        <div className="flex min-h-[144px] items-center justify-center gap-5 border-white px-5 py-5 md:min-h-0 md:justify-start md:gap-[18px] md:border-l-2 md:px-6 md:py-0">
          <p className="font-['Inter'] text-center text-base font-bold leading-[normal] tracking-[-0.3px] text-[#000000]">
            <span className="block whitespace-nowrap">紹介した</span>
            <span className="block whitespace-nowrap">人数</span>
          </p>
          <ProgressContent
            value={data.stats.registeredCount}
            current={data.stats.freeProgressCurrent}
            target={data.stats.freeProgressTarget}
            tone={REFERRAL_NOTICE_TONE.TEAL}
            message={getReferralBonusProgressMessage(
              data.stats.registeredCount,
              data.stats.freeProgressTarget,
              data.stats.freeProgressRemaining,
            )}
          />
        </div>

        <div className="flex min-h-[144px] items-center gap-4 border-white px-5 py-5 md:min-h-0 md:gap-[19px] md:border-l-2 md:px-6 md:py-0">
          <p className="font-['Inter'] text-center text-base font-bold leading-[normal] tracking-[-0.3px] text-[#000000]">
            <span className="block whitespace-nowrap">有料化</span>
            <span className="block whitespace-nowrap">ボーナス</span>
          </p>
          <ProgressContent
            value={data.stats.paidConversionCount}
            current={data.stats.paidMilestoneCurrent}
            target={data.stats.paidMilestoneTarget}
            tone={REFERRAL_NOTICE_TONE.GOLD}
            message={
              paidRemaining > 0
                ? `1名ごとにボーナス獲得。あと${paidRemaining}名で最大12ヶ月の特別ボーナス`
                : "引き続き、1名ごとにボーナス獲得。"
            }
            grow
          />
        </div>
      </div>
    </section>
  );
};
